import { useEffect, useRef, useState } from 'react';
import * as FitRecords from './fitRecordStore';
import { serializeInputs } from './inputSnapshot';
import { currentUserId } from './runCache';

// useInputSession.js
//
// Fixes "refresh returns me to the start and every result is gone" (Sagar,
// client item: Persistence) and makes "Reopen" in Run History refill the form
// (item #23).
//
// The backend ALREADY stores one session per (user, reaction system) —
// GET/PUT /api/session-state, table user_reaction_system_sessions — and
// fitRecordStore already has autosaveFormState / autosaveLastRun /
// loadSessionState for it. Nothing in DataFittingPage was calling them, so
// only module-level singletons (formSessionStore, fittingSessionStore) held
// the state, and those reset on every browser refresh. This hook is the
// missing wiring:
//
//   1. On the first mount of a page load it restores, in priority order:
//        a) a run the user chose to "Reopen" (fitRecordStore.pendingRestore)
//        b) otherwise the last saved session of the most recently used system
//   2. Only AFTER that restore attempt is finished does it start autosaving.
//      Order matters: autosaving first would PUT the page's blank defaults
//      over the saved session — the very thing being restored.
//   3. Autosaves the live inputs (debounced) and the last run's result.
//
// The caller must also hold back its reaction-system signature effect until
// `ready` is true (otherwise page defaults resolve — or create — the wrong
// reaction_systems row before the saved one is known).

// True once this page load has attempted a restore. Module-level on purpose:
// route changes keep it (the in-memory stores still hold the state), a browser
// refresh resets it (the stores are empty again, so restoring is right).
let restoreAttempted = false;

// ── FIX: a NEW TAB must start blank; only a REFRESH may restore ─────────
// `restoreAttempted` is module-level, so every new tab (fresh JS bundle)
// starts with it false and used to fall into "restore the last saved
// session of the most recently used system" — i.e. the previous run's
// inputs reappeared in every new tab, so a new custom system could never
// be started. sessionStorage is the right discriminator: it survives a
// refresh of the same tab but is empty in a newly opened tab. The marker
// is per user, so logging in as someone else in the same tab also starts
// clean. Decided ONCE per page load (module-level) so React StrictMode's
// double-invoked effects and later route changes all see the same answer.
let freshTab = null;
const tabKey = () => `kq_tab_seen:${currentUserId()}`;
function isFreshTab() {
  if (freshTab !== null) return freshTab;
  try {
    freshTab = sessionStorage.getItem(tabKey()) !== '1';
    sessionStorage.setItem(tabKey(), '1');
  } catch (e) {
    freshTab = false;   // storage blocked: keep the old behaviour (restore)
  }
  return freshTab;
}

export function __resetRestoreForTests() { restoreAttempted = false; freshTab = null; }

export function useInputSession({
  enabled,        // false while there's no auth token
  values,         // { field: currentValue } — everything worth snapshotting
  valueDeps,      // array of the same values, for the effect dependency list (constant length)
  serializeOptions,   // { skip, transform } passed to serializeInputs
  applySnapshot,  // (snapshot, { source, label }) => void  — writes fields into the page
  result,         // current fit result (or null)
  loading,        // a run is in flight
  runMeta,        // { reactionSystem, fitScope, label } of the current run
  setResult,      // (resultOrNull) => void
  debounceMs = 800,
}) {
  const [ready, setReady] = useState(false);

  // Latest-value refs so effects don't re-run (or capture stale closures)
  // just because the page re-rendered.
  const valuesRef = useRef(values);        valuesRef.current = values;
  const optionsRef = useRef(serializeOptions); optionsRef.current = serializeOptions;
  const applyRef = useRef(applySnapshot);  applyRef.current = applySnapshot;
  const setResultRef = useRef(setResult);  setResultRef.current = setResult;
  const runMetaRef = useRef(runMeta);      runMetaRef.current = runMeta;
  const activeRunRef = useRef(false);      activeRunRef.current = Boolean(result) || Boolean(loading);

  // The result we last sent to / received from the server — used so a
  // restored result isn't immediately re-uploaded (it can be several MB of
  // base64 plots).
  const savedResultRef = useRef(null);

  // ── 1. restore ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    // a) "Reopen" from Run History. Synchronous, so it's applied before
    //    anything else can touch the form.
    const pending = FitRecords.consumePendingRestore();
    if (pending) {
      restoreAttempted = true;
      if (pending.input_snapshot) {
        applyRef.current(pending.input_snapshot, { source: 'saved-run', label: pending.label });
        // Old results belong to the OLD inputs — don't show them next to the
        // reopened ones.
        savedResultRef.current = null;
        setResultRef.current(null);
      }
      setReady(true);
      return undefined;
    }

    // Brand-new tab (not a refresh): start with a clean page. Nothing is
    // restored, and nothing is wiped either — the other tab's saved session
    // stays intact in the DB; this tab's autosave will attach to whichever
    // reaction system its own inputs resolve to.
    const fresh = isFreshTab();

    // Already restored (or a run is live) earlier in this page load, or this
    // is a fresh tab: the in-memory stores are the source of truth, so leave
    // them alone.
    if (restoreAttempted || activeRunRef.current || fresh) {
      restoreAttempted = true;
      setReady(true);
      return undefined;
    }

    // b) fresh page load → last saved session of the most recently used system
    (async () => {
      try {
        const projectId = await FitRecords.resolveDefaultProjectId();
        // No signature on purpose: the backend then answers with the system
        // used most recently, which is where the user left off.
        const systemId = projectId ? await FitRecords.ensureReactionSystem(projectId) : null;
        const session = systemId ? await FitRecords.loadSessionState(systemId) : null;
        if (cancelled) return;
        if (session && session.form_state) {
          applyRef.current(session.form_state, { source: 'session' });
        }
        if (session && session.last_run_result) {
          savedResultRef.current = session.last_run_result;
          setResultRef.current(session.last_run_result);
        }
        restoreAttempted = true;
      } catch (e) {
        // Best effort — a failed restore just means starting fresh.
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => { cancelled = true; };
  }, [enabled]);

  // ── 2. autosave the live inputs (only once restore has finished) ────
  useEffect(() => {
    if (!enabled || !ready) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const { snapshot } = await serializeInputs(valuesRef.current, optionsRef.current);
      if (!cancelled) FitRecords.autosaveFormState(snapshot);
    }, debounceMs);
    return () => { cancelled = true; clearTimeout(timer); };
    // valueDeps: the caller passes the raw field values so this re-runs when
    // any of them changes (identity check only — no per-render stringify).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ready, debounceMs, ...(valueDeps || [])]);

  // ── 3. autosave the last run's result ───────────────────────────────
  useEffect(() => {
    if (!enabled || !ready || loading) return;
    const current = result || null;
    if (current === savedResultRef.current) return;   // nothing new (or just restored)
    savedResultRef.current = current;
    // null is meaningful too: the user pressed Reset, so the stored "last
    // result" should be cleared rather than resurrected on the next refresh.
    FitRecords.autosaveLastRun(current, runMetaRef.current || null);
  }, [enabled, ready, loading, result]);

  return { ready };
}